import { Shield } from 'lucide-react';
import { ThemeToggle } from '../theme/ThemeToggle';
import { ResponseLengthToggle } from './ResponseLengthToggle';
import { DocumentFilter } from './DocumentFilter';
import { LanguagePicker } from './LanguagePicker';
import { useMe } from '../auth/MeContext';
import { useT } from '../I18nContext';

/**
 * Top rail across the main column. Conversation title on the left,
 * action cluster on the right: document filter + response-length
 * toggle + language picker + theme toggle.
 *
 * Admins also see a "Go to admin portal" cross-link at the start
 * of the action cluster - gated on me.is_admin (which is true for
 * super admins AND per-client admins of the active client).
 * Standard users never see this; if they typed /admin/ into the
 * URL bar they'd be bounced server-side anyway, but hiding the
 * link is the right UX for the common path.
 */
interface RailProps {
  title?: string;
  subtitle?: string;
}

export function Rail({ title, subtitle }: RailProps) {
  const { me } = useMe();
  const t = useT();
  const showAdminLink = !!me?.is_admin;
  // Resolve defaults via useT() instead of JS default params so the
  // catalogue lookup happens at render time and reacts to language
  // changes. Callers that pass an explicit title/subtitle still win.
  const resolvedTitle = title ?? t('rail.default_title');
  const resolvedSubtitle = subtitle ?? t('rail.default_subtitle');

  return (
    <header className="dashboard-rail">
      <div className="dashboard-rail-title">
        <div className="dashboard-rail-title-main">{resolvedTitle}</div>
        <div className="dashboard-rail-title-sub">{resolvedSubtitle}</div>
      </div>
      <div className="dashboard-rail-actions">
        {showAdminLink && (
          <a
            className="dashboard-rail-cross-link"
            href="/admin/"
            title={t('rail.admin_link_title')}
          >
            <Shield size={14} />
            <span>{t('rail.admin_link_label')}</span>
          </a>
        )}
        <DocumentFilter />
        <ResponseLengthToggle />
        <LanguagePicker />
        <ThemeToggle />
      </div>
    </header>
  );
}
